// ============================================================
// useChat (v8.363.3) - send and read the vox.
//
// Subscribes to the lines that name me (the filtered watch), keeps the
// active conversation and the DM chips, and turns the composer's line
// into a document: parse → build → write. A `/roll` in Vox — all is a
// REAL dice-roll log entry through addRollNarrative, exactly as the dice
// tray posts one; in a group or a private vox it stays a chat line with
// the roll attached, because the log has no audience.
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { randomToken } from '../utils/entropy';
import { diag, errorSummary } from '../diagnostics/log';
import {
  buildMessage,
  deriveConversations,
  fromDoc,
  toDoc,
  type BuildContext,
  type ChatChannel,
  type ChatMessage,
  type ConversationKey,
  type ParsedLine,
  type Person,
  type RollPayload,
  type SpeakAs,
} from '../engine/chat';
import { planLine, rollForLine } from '../engine/chat/composerPlan';
import type { CollectionRead } from '../backend/types';
import type { NarrativeEntry } from '../types';
import { keepUnchanged } from '../utils/keepUnchanged';

interface ChatMp {
  onChatUpdate: (
    sessionId: string,
    uid: string,
    isGm: boolean,
    cb: (read: CollectionRead<Record<string, unknown>>) => void,
  ) => () => void;
  addChatMessage: (sessionId: string, id: string, doc: Record<string, unknown>) => Promise<void>;
  tombstoneChatMessage: (sessionId: string, id: string) => Promise<void>;
}

export interface UseChatParams {
  sessionId: string | null;
  me: { uid: string; seat: string; name: string; isGm: boolean } | null;
  gmUid: string | null;
  people: Person[];
  channels: ChatChannel[];
  /** The acting combatant, for in-character lines; null when acting as nobody. */
  actor: SpeakAs | null;
  addRollNarrative: (entry: NarrativeEntry) => void;
  isLocked: (feature: string) => boolean;
  mp: ChatMp;
  mint?: (ts: number, uid: string) => string;
  now?: () => number;
}

type Me = NonNullable<UseChatParams['me']>;
type SetError = Dispatch<SetStateAction<string | null>>;

export default function useChat(params: UseChatParams) {
  const [inCharacter, setInCharacter] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { messages, conversations, active, setActiveState, openDm, closeDm } = useChatChips(params);
  const setActive = useCallback(
    (key: ConversationKey) => {
      setActiveState(key);
      setError(null);
    },
    [setActiveState],
  );
  const send = useSend({ ...params, active, inCharacter, openDm, setError, setInCharacter });
  const remove = useRemove(params.sessionId, params.mp);
  return {
    messages,
    conversations,
    active,
    setActive,
    openDm,
    closeDm,
    send,
    remove,
    inCharacter,
    setInCharacter,
    error,
    clearError: () => setError(null),
  };
}

const inTimeOrder = (read: CollectionRead<Record<string, unknown>>) =>
  read.entries.map((e) => fromDoc(e.id, e.data)).sort((a, b) => a.ts - b.ts || a.id.localeCompare(b.id));

/**
 * The lines that name me (the filtered watch), the chips they make and the
 * active one. A new session or a new me starts from nothing.
 */
function useChatChips({ sessionId, me, mp, channels, people, now = Date.now }: UseChatParams) {
  const [active, setActiveState] = useState<ConversationKey>('table');
  const { openDms, closedDmAt, resetDms, openDm, closeDm } = useDmChips(now, setActiveState);
  const restart = useCallback(() => {
    setActiveState('table');
    resetDms();
  }, [resetDms]);
  const messages = useChatLines({ sessionId, uid: me?.uid, isGm: !!me?.isGm, mp }, restart);
  const conversations = useMemo(
    () =>
      me ? deriveConversations({ messages, channels, me: me.uid, isGm: me.isGm, people, openDms, closedDmAt }) : [],
    [messages, channels, me, people, openDms, closedDmAt],
  );
  // The active chip must exist; a closed or vanished one falls back to the table.
  useEffect(() => {
    if (!conversations.some((c) => c.key === active)) setActiveState('table');
  }, [conversations, active]);
  return { messages, conversations, active, setActiveState, openDm, closeDm };
}

/**
 * The lines that name me, in time order, from nothing for a new session or a new me (`restart` is told). A delivery
 * with the same lines keeps the ones held and sets nothing (plan sync-listeners): every line anywhere re-delivered
 * them all as new objects, and the shell rendered for each. Compared through a ref, never an updater - handed one,
 * React renders once even to keep the same value.
 */
function useChatLines(
  {
    sessionId,
    uid,
    isGm,
    mp,
  }: { sessionId: UseChatParams['sessionId']; uid?: string; isGm: boolean; mp: UseChatParams['mp'] },
  restart: () => void,
): ChatMessage[] {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const held = useRef(messages);
  useEffect(() => {
    held.current = [];
    setMessages(held.current);
    restart();
    if (!sessionId || uid === undefined) return;
    return mp.onChatUpdate(sessionId, uid, isGm, (read) => {
      const next = keepUnchanged(held.current, inTimeOrder(read), (m) => m.id);
      if (next === held.current) return;
      held.current = next;
      setMessages(next);
    });
  }, [sessionId, uid, isGm, mp, restart]);
  return messages;
}

/** The private-vox chips this viewer has open, and when each was closed. */
function useDmChips(now: () => number, setActiveState: Dispatch<SetStateAction<ConversationKey>>) {
  const [openDms, setOpenDms] = useState<string[]>([]);
  const [closedDmAt, setClosedDmAt] = useState<Record<string, number>>({});
  const resetDms = useCallback(() => {
    setOpenDms([]);
    setClosedDmAt({});
  }, []);
  const openDm = useCallback(
    (uid: string) => {
      setOpenDms((d) => (d.includes(uid) ? d : [...d, uid]));
      setClosedDmAt(({ [uid]: _reopened, ...rest }) => rest);
      setActiveState(`dm:${uid}`);
    },
    [setActiveState],
  );
  const closeDm = useCallback(
    (uid: string) => {
      setOpenDms((d) => d.filter((u) => u !== uid));
      setClosedDmAt((c) => ({ ...c, [uid]: now() }));
      setActiveState((a) => (a === `dm:${uid}` ? 'table' : a));
    },
    [now, setActiveState],
  );
  return { openDms, closedDmAt, resetDms, openDm, closeDm };
}

interface SendDeps extends UseChatParams {
  active: ConversationKey;
  inCharacter: boolean;
  openDm: (uid: string) => void;
  setError: SetError;
  setInCharacter: Dispatch<SetStateAction<boolean>>;
}

/**
 * The composer's send: the plan decides (engine/chat/composerPlan), then
 * a mask toggle, a refusal with its sentence, or a line delivered.
 * Resolves true when the composer may clear.
 */
function useSend(p: SendDeps) {
  const { sessionId, me, isLocked, people, channels, active, actor, setError, setInCharacter } = p;
  const deliver = useDeliver(p);
  return useCallback(
    async (raw: string): Promise<boolean> => {
      if (!sessionId || !me) return false;
      const plan = planLine(raw, { isLocked, isGm: me.isGm, people, channels, me: me.uid, active, actor });
      if (plan.kind === 'refuse') {
        setError(plan.error);
        return false;
      }
      if (plan.kind === 'mask') {
        setInCharacter(plan.inCharacter);
        setError(plan.error);
        return true;
      }
      return deliver(plan.parsed, sessionId, me);
    },
    [sessionId, me, isLocked, people, channels, active, actor, setError, setInCharacter, deliver],
  );
}

/** Everything delivering one line touches. */
interface DeliverEnv {
  active: ConversationKey;
  addRollNarrative: (entry: NarrativeEntry) => void;
  compose: (parsed: ParsedLine, me: Me, ts: number, roll: RollPayload | null) => ChatMessage | null;
  openDm: (uid: string) => void;
  setError: SetError;
  mp: ChatMp;
}

/**
 * Roll and write a line stamped `ts`. A /roll in Vox - all goes to the log
 * instead (rollForLine); a whisper opens its chip.
 */
async function deliverLine(env: DeliverEnv, parsed: ParsedLine, ts: number, sessionId: string, me: Me) {
  // A plain line carries no roll; a /roll either carries one or went to the log.
  const rolled = parsed.kind === 'roll' ? rollForLine(parsed.expr, env.active, ts) : { roll: null };
  if (!rolled) return false;
  if ('narrative' in rolled) {
    env.addRollNarrative(rolled.narrative);
    env.setError(null);
    return true;
  }
  const msg = env.compose(parsed, me, ts, rolled.roll);
  if (!msg) return false;
  if (parsed.kind === 'whisper') env.openDm(parsed.uid);
  env.setError(null);
  return writeLine(env.mp, sessionId, msg, env.setError);
}

/** Stamp a line on a per-client monotonic clock and deliver it. */
function useDeliver(p: SendDeps) {
  const { active, addRollNarrative, mp, now = Date.now, openDm, setError } = p;
  const lastTs = useRef(0);
  const compose = useComposer(p);
  return useCallback(
    (parsed: ParsedLine, sessionId: string, me: Me): Promise<boolean> => {
      // Monotonic, so two quick lines never share an id.
      const ts = Math.max(now(), lastTs.current + 1);
      lastTs.current = ts;
      return deliverLine({ active, addRollNarrative, compose, openDm, setError, mp }, parsed, ts, sessionId, me);
    },
    [active, addRollNarrative, mp, now, openDm, setError, compose],
  );
}

/** Build the message document for a parsed line, as this seat and this mask. */
function useComposer({ gmUid, people, channels, actor, inCharacter, active, mint }: SendDeps) {
  return useCallback(
    (parsed: ParsedLine, me: Me, ts: number, roll: RollPayload | null) => {
      const ctx: BuildContext = {
        me,
        gmUid,
        speakAs: inCharacter ? actor : null,
        channels,
        people,
        activeConv: active,
        now: ts,
        mint: () => (mint ? mint(ts, me.uid) : `${ts}-${me.uid.slice(0, 8)}-${randomToken(4)}`),
      };
      return buildMessage(parsed, ctx, roll);
    },
    [gmUid, people, channels, actor, inCharacter, active, mint],
  );
}

/** Write one line; a failure is logged and said, never thrown. */
async function writeLine(mp: ChatMp, sessionId: string, msg: ChatMessage, setError: SetError): Promise<boolean> {
  try {
    await mp.addChatMessage(sessionId, msg.id, toDoc(msg) as unknown as Record<string, unknown>);
    return true;
  } catch (e) {
    diag.error('chat', 'send failed', errorSummary(e));
    setError('The line did not reach the table - try again.');
    return false;
  }
}

/** Tombstone a line; a failure is logged, never thrown. */
function useRemove(sessionId: string | null, mp: ChatMp) {
  return useCallback(
    async (id: string) => {
      if (!sessionId) return;
      try {
        await mp.tombstoneChatMessage(sessionId, id);
      } catch (e) {
        diag.error('chat', 'tombstone failed', errorSummary(e));
      }
    },
    [sessionId, mp],
  );
}
